import { inject, Injectable } from "@angular/core";
import { TaxesRemote } from "@modules/taxes/domain/infrastructure/taxes.remote";
import { TaxesFacade } from "@modules/taxes/domain/+state/taxes.facade";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import {
	CapitalGainsTaxModel,
	DeclaredTaxesModel,
	SelfCertificationModel,
	TaxesTransactionTypes
} from "@modules/taxes/domain/entities/taxes.model";
import { catchError, map, take } from "rxjs/operators";
import { TaxesTrackerFacade } from "@modules/taxes/domain/+state/taxes-tracker-facade.service";
import { TAXES_TRX_DETAIL_VIEWED } from "@modules/taxes/domain/entities/taxes-tracking.model";
import { DepositCellEvent } from "@modules/taxes/features/taxes-multi-declaration/deposit-list/deposit-cell/deposit-cell.component";
import { TaxesMultiDeclarationFacade } from "@modules/taxes/domain/+state/taxes-multi-declaration.facade";
import { FiatService } from "@main-services/currency";
import { TransactionInfo } from "@modules/taxes/domain/entities/transaction-info.model";

export interface TransactionTaxesModel {
	declaredTrx: DeclaredTaxesModel;
	isUserEligible: boolean;
}

export interface CapitalGainsTaxesModel extends CapitalGainsTaxModel {
	isError: boolean;
}

@Injectable()
export class TransactionTaxesFacade {
	private readonly taxesTrackerFacade = inject(TaxesTrackerFacade);
	private readonly taxesMultiFacade = inject(TaxesMultiDeclarationFacade);
	private readonly taxesFacade = inject(TaxesFacade);
	private readonly taxesRemote = inject(TaxesRemote);
	private readonly fiatService = inject(FiatService);

	private taxesTrxStatus$$: BehaviorSubject<DeclaredTaxesModel> = new BehaviorSubject<DeclaredTaxesModel>({
		fiat_id: "",
		status: null,
		declarations: [],
		fiatId: ""
	});
	private taxesTrxStateInfo$: Observable<DeclaredTaxesModel> = this.taxesTrxStatus$$.asObservable();

	public taxesInfoVM$: Observable<TransactionTaxesModel> = combineLatest({
		declaredTrx: this.taxesTrxStateInfo$,
		isUserEligible: this.taxesFacade.taxStatus$.pipe(map((val) => val === SelfCertificationModel.Eligible))
	});

	public getCapitalGainsTax$(trxId: string, isFiatTrx: boolean): Observable<CapitalGainsTaxesModel> {
		return this.taxesRemote.getCapitalGainsTax$(trxId, isFiatTrx).pipe(
			map((response) => ({ ...response, isError: false })),
			catchError(() => {
				return [{ capitalGain: null, taxableAmount: null, isError: true }];
			})
		);
	}

	public getTaxesTransactionInfo(trxId: string): void {
		this.taxesFacade.isTaxesActive$.pipe(take(1)).subscribe((isActive) => {
			if (isActive) {
				this.fetchTaxesTrxInfo$(trxId);
			}
		});
	}

	private buildNewModel(response: DeclaredTaxesModel): DeclaredTaxesModel {
		const fiatId = this.fiatService
			.getList()
			.getValue()
			.find((f) => f.getPid() === response.fiat_id)
			?.getId();
		return { ...response, fiatId: fiatId };
	}

	private fetchTaxesTrxInfo$(transactionId: string): void {
		this.taxesRemote.getTaxesInfo$(transactionId).subscribe({
			next: (response) => {
				this.taxesTrxStatus$$.next(this.buildNewModel(response));
				// Fetch undeclared transactions if the transaction is Undeclared
				response.status === TaxesTransactionTypes.Undeclared
					? this.taxesMultiFacade.fetchUndeclaredTransactions(true)
					: null;

				// Call segment tracking if the transaction is Declared
				response.status === TaxesTransactionTypes.Declared
					? this.taxesTrackerFacade.tracker(TAXES_TRX_DETAIL_VIEWED)
					: null;
			}
		});
	}

	public onShareTrxClicked(trxInfo: DepositCellEvent): void {
		if (this.taxesMultiFacade.getApiFail()) {
			this.taxesMultiFacade.fetchUndeclaredTransactions(true, trxInfo, "true");
		} else {
			this.taxesMultiFacade.setTrxData(trxInfo, "true", null, null);
		}
	}

	public getTransactionInfo(id: string): Observable<TransactionInfo> {
		return this.taxesRemote.getTransactionInfo(id);
	}
}
